/**
 * Pure H OS · Sprint 12 · Commerce mocks — types + live views.
 * Payload now lives in MongoDB `pureh_*` collections.
 */
import { liveArray, liveSingleton } from "../services/store";

// ── Orders ───────────────────────────────────────────────────────
export type OrderStatus = "pending" | "paid" | "packed" | "shipped" | "delivered" | "refunded" | "cancelled";
export type OrderChannel = "web" | "pop-up" | "wholesale" | "affiliate" | "gift";
export type OrderLine = { sku: string; name: string; qty: number; unitEGP: number };
export type Order = {
  id: string;
  reference: string;
  customer: string;
  city: string;
  status: OrderStatus;
  channel: OrderChannel;
  placedRelative: string;
  totalEGP: number;
  lines: OrderLine[];
  paymentMethod: "card" | "cash-on-delivery" | "wallet" | "invoice";
  aiFlag?: string;
};
export const ORDERS: Order[] = liveArray<Order>("orders");

// ── Products ─────────────────────────────────────────────────────
export type ProductStatus = "live" | "draft" | "archived" | "coming-soon";
export type Product = {
  id: string;
  sku: string;
  name: string;
  family: "eau-de-parfum" | "discovery" | "gift-set";
  priceEGP: number;
  compareAtEGP?: number;
  status: ProductStatus;
  stock: number;
  sold30d: number;
  rating: number;
  reviews: number;
  hero: string;
  notes: string;
};
export const PRODUCTS: Product[] = liveArray<Product>("products");

// ── Inventory ────────────────────────────────────────────────────
export type StockLevel = "critical" | "low" | "healthy" | "overstocked";
export type StockRow = {
  sku: string;
  name: string;
  onHand: number;
  incoming: number;
  reserved: number;
  reorderPoint: number;
  daysCover: number;
  level: StockLevel;
  location: string;
};
export const STOCK: StockRow[] = liveArray<StockRow>("stock");

// ── Pricing rules ────────────────────────────────────────────────
export type PriceRuleKind =
  | "tiered" | "member" | "geo" | "flash"
  | "retail" | "wholesale" | "distributor" | "vip" | "staff"
  | "marketplace" | "export" | "seasonal" | "customer_specific";
export type PriceRule = {
  id: string;
  name: string;
  scope: "all" | "family" | "sku";
  target: string;
  kind: PriceRuleKind;
  detail: string;
  activeFrom: string;
  activeTo: string;
  active: boolean;
  // v1 · added by Wave 1 · optional fields; back-compat safe
  priority?: number;
  marginPct?: number;
  effectiveDate?: string;
  approvalStatus?: "draft" | "pending" | "approved" | "rejected";
  approvedBy?: string;
};
export const PRICE_RULES: PriceRule[] = liveArray<PriceRule>("price_rules");

// ── Promotions ───────────────────────────────────────────────────
export type PromoKind = "percentage" | "fixed" | "bundle" | "buy_x_get_y" | "free_shipping" | "gift" | "coupon" | "automatic";
export type Promo = {
  id: string;
  code: string;
  label: string;
  discount: string;
  status: "active" | "scheduled" | "expired" | "draft" | "paused";
  redemptions: number;
  cap?: number;
  window: string;
  revenueEGP: number;
  // v1 · added by Wave 1 · optional fields; back-compat safe
  kind?: PromoKind;
  productSkus?: string[];      // Product/SKU selection
  collections?: string[];      // Collection selection
  discountValue?: number;      // % or EGP depending on kind
  buyQty?: number;             // for buy_x_get_y
  getQty?: number;             // for buy_x_get_y
  giftSku?: string;            // for gift kind
  budgetEGP?: number;
  expectedRevenueEGP?: number;
  expectedMarginPct?: number;
  actualRevenueEGP?: number;
  roas?: number;
  approvalStatus?: "draft" | "pending" | "approved" | "rejected";
  approvedBy?: string;
  autoApply?: boolean;         // for automatic kind
};
export const PROMOS: Promo[] = liveArray<Promo>("promos");

// ── Shopify Sync ─────────────────────────────────────────────────
export type SyncStream = {
  key: string;
  label: string;
  lastSyncRelative: string;
  status: "ok" | "warning" | "error" | "not-connected";
  itemsSynced: number;
  itemsPending: number;
  note: string;
};
export const SYNC_STREAMS: SyncStream[] = liveArray<SyncStream>("sync_streams");

// ── Customer Purchase Signals ────────────────────────────────────
export type SignalKind = "cart-abandon" | "reorder-window" | "high-ltv" | "at-risk" | "cross-sell" | "vip";
export type PurchaseSignal = {
  id: string;
  kind: SignalKind;
  customer: string;
  city: string;
  detail: string;
  ltv: number;
  lastOrderRelative: string;
  suggestedAction: string;
  aiConfidence: number;
};
export const SIGNALS: PurchaseSignal[] = liveArray<PurchaseSignal>("signals");

// ── Overview KPIs & AI signals (singleton) ──────────────────────
type Kpi = { key: string; label: string; value: string; delta: number; trend: string; hint?: string };
type TopProduct = { sku: string; name: string; unitsSold: number; revenueEGP: number };
type RevByDay = { d: string; v: number };
export type CommerceOverview = { kpis: Kpi[]; topProducts: TopProduct[]; revenueByDay: RevByDay[] };
export const COMMERCE_OVERVIEW: CommerceOverview = liveSingleton<CommerceOverview>(
  "commerce_overview",
  { kpis: [], topProducts: [], revenueByDay: [] },
);
